import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useQuery as useReactQuery } from '@tanstack/react-query';
import type { GridApi } from 'ag-grid-community';
import { CellClassParams, ColDef, ColGroupDef } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { InputNumber } from 'primereact/inputnumber';
import { ProgressBar } from 'primereact/progressbar';
import { TabPanel, TabView } from 'primereact/tabview';
import { Toast } from 'primereact/toast';
import { Tooltip } from 'primereact/tooltip';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { useAppSelector } from '@/app/hooks';
import { ChuteSchedulerActions } from '@/app/slices/chuteScheduler';
import { SitesDropdown } from '@/components';
import { parseExistingComments } from '@/components/common/revisions-button/utils';
import { exportGridToExcel } from '@/hooks/useExcelExport';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { getValidSites, useAccessToken } from '@/services/auth';
import {
  allTopRevisionsOptions,
  chuteScheduleCombinedOptions,
  updateRevisions as updateRevisionsRest,
} from '@/services/chute-scheduler/api';
import { useContextMenu } from '@/utils/context-menu';

import RevisionsButton from '../../components/common/revisions-button/RevisionsButton';
import { ApplyButton } from './components/ApplyButton';
import { ChuteSchedulerCalendar as Calendar } from './components/Calendar';
import { Grid } from './components/Grid';
import { Interval } from './components/Interval';
import RevisionActions from './components/RevisionActions';
import { SaveButton } from './components/SaveButton';
import { StartTime } from './components/StartTime';
import { resultsColumnDefs, schedulingColumnDefs } from './utils/columnDefs';
import {
  COLOR_CLASSES,
  COLUMNS_TO_SHADE,
  EXPORT_COLOR_RULES,
} from './utils/constants';

import './index.css';

export type ChuteSchedulerProps = { className?: string };

const fade = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
};

export const ChuteSchedulerLegacyPage = ({
  className,
}: ChuteSchedulerProps) => {
  const [params] = useSearchParams();
  const site = params.get('site');
  const date = params.get('date') as string;
  const tabViewClassName = clsx('tabview-no-panels', 'tab-view-shading');

  const dispatch = useDispatch();
  const token = useAccessToken();
  const toastRef = useRef<Toast | null>(null);
  const gridRef = useRef<AgGridReact>(null);

  const [showAllTopRevisionNumbersDialog, setShowAllTopRevisionNumbersDialog] =
    useState(false);
  const [showAddRevisionCommentsDialog, setShowAddRevisionCommentsDialog] =
    useState(false);
  const [revisionComments, setRevisionComments] = useState<string>('');

  const { t } = useTranslation();

  const me = useAppSelector((state) => state.authentication.me);

  const isOneSelectedRow =
    gridRef.current?.api?.getSelectedNodes().length === 1;

  const handleSetValue = async (fieldName: string, value: string) => {
    if (fieldName === 'revisionComments') {
      setRevisionComments(value);

      const selectedNodes = gridRef.current?.api?.getSelectedNodes();
      if (!selectedNodes || selectedNodes.length === 0) {
        console.warn('No rows selected in the grid');
        return;
      }

      const newComments = parseExistingComments(value);

      const updates: Array<{
        donorScheduleId: string;
        revisionComment: string;
      }> = [];

      selectedNodes.forEach((node) => {
        const rowData = node.data;
        if (rowData?.donorScheduleId) {
          const existingComments = parseExistingComments(
            rowData?.revisionComment || '',
          );

          const commentsToAdd = newComments.filter(
            (newComment) => !existingComments.includes(newComment),
          );

          commentsToAdd.forEach((comment) => {
            updates.push({
              donorScheduleId: rowData.donorScheduleId,
              revisionComment: comment,
            });
          });
        }
      });

      if (updates.length > 0) {
        await updateRevisionsRest(token, updates);
        refetch?.();
        refetchAllTopRevisions?.();
      }
    }
  };

  const handleGetValue = (fieldName: string) => {
    if (fieldName === 'revisionComments') {
      const selectedNodes = gridRef.current?.api?.getSelectedNodes();
      if (selectedNodes && selectedNodes.length === 1) {
        const rowData = selectedNodes[0].data;
        return rowData?.revisionComment || '';
      }
      return revisionComments;
    }
    return null;
  };

  const { schedulingRows, resultsRows, multiChuteCount, error, loading } =
    useAppSelector((s) => s.chuteScheduler);

  const {
    data: restData,
    refetch: refetchRest,
    isLoading: restLoading,
    isError: restError,
  } = useReactQuery({
    ...chuteScheduleCombinedOptions(token, { siteId: site!, date: date! }),
    enabled: !!token && !!site && !!date,
  });

  const { data: restTopRevisions, refetch: refetchRestTopRevisions } =
    useReactQuery({
      ...allTopRevisionsOptions(token, { date: date! }),
      enabled: !!token && !!date,
      refetchInterval: date ? 20000 : false,
    });

  useEffect(() => {
    dispatch({
      type: ChuteSchedulerActions.SET_LOADING,
      loading: restLoading,
    });
  }, [restLoading, dispatch]);

  useEffect(() => {
    if (restError) {
      dispatch({
        type: ChuteSchedulerActions.SET_ERROR,
        error: 'Unable to load schedule. Try another date and/or site.',
      });
    }
  }, [restError, dispatch]);

  useEffect(() => {
    if (!restData) return;

    if (!restData.scheduling.length && !restData.results.length) {
      dispatch({
        type: ChuteSchedulerActions.SET_ERROR,
        error: 'No records found. Try another date and/or site.',
      });
      dispatch({
        type: ChuteSchedulerActions.UPDATE_SCHEDULING_ROWS,
        rows: [],
      });
      dispatch({ type: ChuteSchedulerActions.UPDATE_RESULTS_ROWS, rows: [] });
      dispatch({
        type: ChuteSchedulerActions.UPDATE_SELECTED_SCHEDULING_ROWS,
        rows: [],
      });
      dispatch({
        type: ChuteSchedulerActions.UPDATE_SELECTED_RESULTS_ROWS,
        rows: [],
      });
      return;
    }

    dispatch({
      type: ChuteSchedulerActions.UPDATE_SCHEDULING_ROWS,
      rows: restData.scheduling,
    });
    dispatch({
      type: ChuteSchedulerActions.UPDATE_RESULTS_ROWS,
      rows: restData.results,
    });
    dispatch({
      type: ChuteSchedulerActions.UPDATE_SELECTED_SCHEDULING_ROWS,
      rows: [],
    });
    dispatch({
      type: ChuteSchedulerActions.UPDATE_SELECTED_RESULTS_ROWS,
      rows: [],
    });
  }, [restData, dispatch]);

  const refetch = refetchRest;
  const allTopRevisionNumbers = { allTopRevisions: restTopRevisions };
  const refetchAllTopRevisions = refetchRestTopRevisions;

  useEffect(() => {
    if (error) {
      toastRef.current?.show({
        severity: 'warn',
        summary: 'No data returned',
        detail: error,
        life: 6000,
      });
      dispatch({ type: ChuteSchedulerActions.SET_ERROR, error: null });
    }
  }, [error, dispatch]);

  const [activeIndex, setActiveIndex] = useState(0);
  const [isShaded, setIsShaded] = useState(false);

  const rows = useMemo(
    () => (activeIndex === 0 ? schedulingRows : resultsRows),
    [activeIndex, schedulingRows, resultsRows],
  );

  const exportKeepColor = useFeatureFlag('Excel_Keep_Color');

  const columnDefs = useMemo(
    () =>
      (activeIndex === 0 ? schedulingColumnDefs : resultsColumnDefs).map(
        (col) => (col.field === 'embryoPlan' ? { ...col, hide: true } : col),
      ),
    [activeIndex],
  );

  const gridApiRef = useRef<GridApi | null>(null);

  const handleExport = () => {
    const api = gridRef?.current?.api;
    if (api) {
      exportGridToExcel(api, {
        sheetName: activeIndex === 0 ? 'Scheduling' : 'Results',
        filename: `chute-schedule-${date}-${activeIndex === 0 ? 'scheduling' : 'results'}.xlsx`,
        preserveColors: exportKeepColor,
      });
    }
  };

  const handleRevert = () => {
    confirmDialog({
      header: 'Revert changes?',
      message:
        'This will discard all unsaved changes and reload the schedule from the database. Continue?',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Yes, Revert',
      rejectLabel: 'Cancel',
      accept: async () => {
        const res = await refetch();
        if (res.data) {
          dispatch({
            type: ChuteSchedulerActions.UPDATE_SCHEDULING_ROWS,
            rows: res.data.scheduling,
          });
          dispatch({
            type: ChuteSchedulerActions.UPDATE_RESULTS_ROWS,
            rows: res.data.results,
          });
        }
      },
    });
  };

  const [filterOpen, setFilterOpen] = useState<number | null>(() => {
    if (typeof window !== 'undefined') {
      return window.matchMedia('(min-width: 768px)').matches ? 0 : null; // open on desktop
    }
    return null;
  });

  const generateColorCombinations = useCallback((api: GridApi<any>) => {
    const combinationColors = new Map<string, string>();
    const colorValues = Object.values(COLOR_CLASSES);

    const allCombinations = new Set<string>();
    api.forEachNode((node) => {
      const sire = node?.data?.sire || '';
      const semenType = node?.data?.semenType || '';
      if (sire) allCombinations.add(`${sire}-${semenType}`);
    });

    Array.from(allCombinations).forEach((combo, index) => {
      combinationColors.set(combo, colorValues[index % colorValues.length]);
    });

    return { combinationColors, colorValues };
  }, []);

  const assignNodeColors = useCallback(
    (api: GridApi<any>, combinationColors: Map<string, string>) => {
      const nodeColors = new Map<string, string>();
      api.forEachNode((node) => {
        const sire = node?.data?.sire || '';
        const semenType = node?.data?.semenType || '';
        const color = combinationColors.get(`${sire}-${semenType}`);
        if (color) nodeColors.set(node.id as string, color);
      });
      return nodeColors;
    },
    [],
  );

  const updateColumnDefinitions = useCallback(
    (
      api: GridApi<any>,
      nodeColors: Map<string, string>,
      colorValues: string[],
      isClearing: boolean,
    ): (ColDef<any, any> | ColGroupDef<any>)[] => {
      if (isClearing) {
        const uncoloredColumns = api.getColumns()?.map((col) => ({
          ...col.getColDef(),
          cellClassRules: {},
        }));

        return uncoloredColumns as (ColDef<any, any> | ColGroupDef<any>)[];
      }

      const updatedColumns = api.getColumns()?.map((col) => {
        const colDef = col.getColDef();
        const field = colDef.field;

        if (COLUMNS_TO_SHADE.includes(field!)) {
          return {
            ...colDef,
            cellClassRules: Object.fromEntries(
              colorValues.map((color) => [
                color,
                (params: CellClassParams) =>
                  nodeColors.get(params.node.id!) === color,
              ]),
            ),
          };
        }

        if (field === 'export') {
          return {
            ...colDef,
            cellClassRules: {
              ...Object.entries(EXPORT_COLOR_RULES).reduce(
                (rules, [key, color]) => ({
                  ...rules,
                  [color]: (params: CellClassParams) =>
                    key === 'default'
                      ? !params.value || params.value === ''
                      : params.value?.includes(key),
                }),
                {},
              ),
            },
          };
        }
        if (field === 'pooled') {
          return {
            ...colDef,
            cellClassRules: {
              'bg-tog-light-blue-shaded': (params: CellClassParams) =>
                params.value !== 'No',
            },
          };
        }

        if (field === 'inMat') {
          return {
            ...colDef,
            cellClassRules: {
              'tog-pink-background': (params: CellClassParams) =>
                params.node.data.inMat >= 40,
            },
          };
        }

        return {
          ...colDef,
          cellClassRules: {},
        };
      });

      return updatedColumns as (ColDef<any, any> | ColGroupDef<any>)[];
    },
    [],
  );

  const handleShadeRows = useCallback(() => {
    if (!gridRef.current?.api) return;
    const api = gridRef.current.api;

    if (!isShaded) {
      const { combinationColors, colorValues } = generateColorCombinations(api);
      const nodeColors = assignNodeColors(api, combinationColors);
      const updatedColumns = updateColumnDefinitions(
        api,
        nodeColors,
        colorValues,
        false,
      );

      if (updatedColumns) {
        api.setGridOption('columnDefs', updatedColumns);
      }
    } else {
      const uncoloredColumns = updateColumnDefinitions(
        api,
        new Map(),
        [],
        true,
      );
      if (uncoloredColumns) {
        api.setGridOption('columnDefs', uncoloredColumns);
      }
    }

    api.refreshCells();
    setIsShaded((prev) => !prev);
  }, [
    assignNodeColors,
    generateColorCombinations,
    isShaded,
    updateColumnDefinitions,
  ]);

  const { getContextMenuItems: schedulingContextMenu } = useContextMenu({
    idKey: 'aspirationId',
  });
  const { getContextMenuItems: resultsContextMenu } = useContextMenu({
    idKey: 'donorScheduleId',
  });
  const getContextMenuItems =
    activeIndex === 0 ? schedulingContextMenu : resultsContextMenu;

  const validSites = getValidSites(me);

  return (
    <div
      className={`chute-scheduler-page ${className ?? ''} h-[100vh - 80px] flex flex-col`}
    >
      <ConfirmDialog />
      <Toast ref={toastRef} position="bottom-center" />

      <Tooltip
        target=".chute-help"
        content="Number of parallel chutes to use when scheduling. Increase this to split donors across multiple concurrent chutes."
        position="top"
        className="max-w-xs"
      />

      <Accordion
        multiple={false}
        activeIndex={filterOpen}
        onTabChange={(e) =>
          setFilterOpen(Array.isArray(e.index) ? e.index[0] : e.index)
        }
        className="mb-2"
      >
        <AccordionTab header="Filters & Actions">
          <div className="table-background flex w-full flex-col gap-4 rounded-t-lg md:flex-row">
            <div className="flex w-full flex-col gap-3">
              <div className="flex flex-wrap gap-2">
                <Calendar className="w-full sm:w-auto" />
                <SitesDropdown
                  className="w-full sm:w-auto"
                  field="siteName"
                  label={t('site.site')}
                  paramKey="site"
                  validSites={validSites}
                />
              </div>

              <div className="flex flex-wrap gap-2">
                <Button
                  label="Refresh"
                  icon="pi pi-refresh"
                  className="p-button-danger h-10 w-full sm:w-auto"
                  onClick={handleRevert}
                />
                <Button
                  label="Export"
                  icon="pi pi-file-excel"
                  className="p-button-secondary h-10 w-full sm:w-auto"
                  onClick={handleExport}
                />
                <Button
                  label={isShaded ? 'Unshade Rows' : 'Shade Rows'}
                  className="p-button-secondary h-10 w-full sm:w-auto"
                  onClick={handleShadeRows}
                />
              </div>
            </div>

            {activeIndex === 0 && (
              <div className="flex w-full flex-col gap-3 md:max-w-none">
                <div className="flex flex-wrap justify-start gap-2 lg:flex-nowrap lg:justify-end">
                  <StartTime className="w-full lg:w-auto" />
                  <Interval className="w-full lg:w-auto" />

                  <div className="flex w-full flex-col gap-1 md:w-fit">
                    <div className="flex items-center gap-1">
                      <span>Chutes</span>
                      <i className="pi pi-info-circle chute-help cursor-help text-gray-400" />
                    </div>

                    <InputNumber
                      value={multiChuteCount}
                      onValueChange={(e) =>
                        dispatch({
                          type: ChuteSchedulerActions.UPDATE_MULTI_CHUTE_COUNT,
                          count: e.value ?? 1,
                        })
                      }
                      min={1}
                      max={20}
                      showButtons
                      className="h-10 w-full md:w-20"
                    />
                  </div>
                </div>

                <div className="flex flex-wrap justify-end gap-2">
                  <ApplyButton className="h-10 w-full sm:w-auto" />
                  <SaveButton className="h-10 w-full sm:w-auto" />
                </div>
              </div>
            )}
            {activeIndex === 1 && (
              <div className="flex w-full flex-row items-end justify-end gap-2">
                <Button
                  label="All Revision Numbers"
                  icon="pi pi-eye"
                  severity="secondary"
                  className="p-button-secondary h-10 w-full sm:w-auto"
                  onClick={() => {
                    setShowAllTopRevisionNumbersDialog(true);
                  }}
                />
                <RevisionsButton
                  showAddRevisionCommentsDialog={showAddRevisionCommentsDialog}
                  setShowAddRevisionCommentsDialog={
                    setShowAddRevisionCommentsDialog
                  }
                  handleGetValue={handleGetValue}
                  handleSetValue={handleSetValue}
                  disabled={!isOneSelectedRow}
                  className="p-button-secondary h-10 w-full sm:w-auto"
                />
                <RevisionActions
                  resultsRows={resultsRows}
                  gridRef={gridRef}
                  label="Revision Actions"
                  icon="pi pi-cog"
                  severity="secondary"
                  className="p-button-secondary h-10 w-full sm:w-auto"
                  disabled={rows.length < 1 || activeIndex !== 1}
                  refetch={refetch}
                />
                <Dialog
                  visible={showAllTopRevisionNumbersDialog}
                  onHide={() => setShowAllTopRevisionNumbersDialog(false)}
                  header="All Top Revision Numbers Per Fertilization Site"
                  className="w-[80vw]"
                >
                  <DataTable
                    value={allTopRevisionNumbers?.allTopRevisions ?? []}
                    scrollable
                    scrollHeight="400px"
                    className="w-full"
                  >
                    <Column field="revisionNumber" header="Revision Number" />
                    <Column
                      field="fertilizationSiteName"
                      header="Fertilization Site Name"
                    />
                  </DataTable>
                </Dialog>
              </div>
            )}
          </div>
        </AccordionTab>
      </Accordion>

      <TabView
        className={tabViewClassName}
        activeIndex={activeIndex}
        onTabChange={(e) => setActiveIndex(e.index)}
      >
        <TabPanel header="Scheduling" />
        <TabPanel header="Results" />
      </TabView>

      <div className="table-background rounded-b-lg p-4">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeIndex}
            variants={fade}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.25 }}
            className="h-[70vh] overflow-x-auto md:h-[60vh]"
          >
            {loading ? (
              <ProgressBar
                mode="indeterminate"
                style={{ height: '4px', borderRadius: 0 }}
              />
            ) : (
              <Grid
                refreshData={refetch}
                columnDefs={columnDefs}
                rowData={rows}
                gridKey={activeIndex}
                setGridApi={(api: GridApi | null) => (gridApiRef.current = api)}
                gridRef={gridRef}
                date={date}
                getContextMenuItems={getContextMenuItems}
              />
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};
